import axios from 'axios'
import type { RegisterUserInput } from '@/contexts/auth-types'
import type { Property } from '@/types'
import { API_BASE_URL } from '@/lib/config'
import { getAppCheckToken } from './firebase'
import { storage, clearLegacyUserProfileCache } from '@/utils/storage'
import { clearAuthScopedSessionCaches } from '@/utils/authSessionCache'
import { signOutFirebaseAuth } from '@/lib/firebaseAuthBridge'
import type { WalletBalance } from '@/lib/money'

type ErrorPayload = {
    detail?: any
    message?: string
    error?: string
}

function buildValidationMessage(detail: any): string | null {
    if (!Array.isArray(detail) || detail.length === 0) return null
    const first = detail[0] || {}
    const type = String(first?.type || '')
    const loc = Array.isArray(first?.loc) ? first.loc.filter(Boolean).join('.') : 'field'
    const friendlyFieldLabel =
        /^body\.messages\.\d+\.content$/.test(loc) || loc === 'body.message'
            ? 'Your message'
            : loc
    const maxLength = first?.ctx?.max_length

    if (type.includes('string_too_long') && typeof maxLength === 'number') {
        return `${friendlyFieldLabel} is too long. Maximum allowed length is ${maxLength} characters.`
    }

    if (typeof first?.msg === 'string' && first.msg.trim()) {
        return `${friendlyFieldLabel}: ${first.msg}`
    }

    return null
}

export function getApiErrorMessage(error: any, fallback: string = 'Request failed. Please try again.'): string {
    // Axios-style error
    const data: ErrorPayload | undefined = error?.response?.data
    if (data) {
        if (Array.isArray(data.detail)) {
            const validationMessage = buildValidationMessage(data.detail)
            if (validationMessage) return validationMessage
        }
        if (typeof data.detail === 'string' && data.detail.trim()) return data.detail
        if (typeof data.message === 'string' && data.message.trim()) return data.message
        if (typeof data.error === 'string' && data.error.trim()) return data.error
    }

    if (typeof error?.message === 'string' && error.message.trim()) return error.message
    return fallback
}

export function getApiErrorMessageFromResponse(
    status: number,
    data: ErrorPayload | undefined,
    fallback: string = 'Request failed. Please try again.'
): string {
    // Never expose raw upstream/provider errors for server-side failures.
    if (status >= 500) {
        return 'The AI service is temporarily unavailable. Please try again shortly.'
    }

    if (status === 413) {
        return (
            (typeof data?.detail === 'string' && data.detail) ||
            'Your request is too large. Please reduce the input size and try again.'
        )
    }

    if (Array.isArray(data?.detail)) {
        const validationMessage = buildValidationMessage(data.detail)
        if (validationMessage) return validationMessage
    }

    if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail
    if (typeof data?.message === 'string' && data.message.trim()) return data.message
    if (typeof data?.error === 'string' && data.error.trim()) return data.error
    return fallback
}

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
})

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []
let refreshFailureSubscribers: ((error: any) => void)[] = []
let authTokenSync: ((token: string | null) => void) | null = null

/** Keep React auth state aligned with axios silent refresh (Pusher, web push, etc.). */
export function registerAuthTokenSync(fn: (token: string | null) => void): void {
    authTokenSync = fn
}

function onRefreshed(token: string) {
    refreshSubscribers.forEach((cb) => cb(token))
    refreshSubscribers = []
    refreshFailureSubscribers = []
}

function addRefreshSubscriber(cb: (token: string) => void) {
    refreshSubscribers.push(cb)
}

function onRefreshFailed(error: any) {
    refreshFailureSubscribers.forEach((cb) => cb(error))
    refreshSubscribers = []
    refreshFailureSubscribers = []
}

function addRefreshFailureSubscriber(cb: (error: any) => void) {
    refreshFailureSubscribers.push(cb)
}

/** Auth + App Check headers for axios and raw fetch (chat/GenUI paths). */
export async function getApiRequestHeaders(
    extra: Record<string, string> = {},
): Promise<Record<string, string>> {
    const headers: Record<string, string> = { ...extra }
    const token = storage.local.getItem('access_token')
    if (token) {
        headers.Authorization = `Bearer ${token}`
    }
    try {
        const appCheckToken = await getAppCheckToken()
        if (appCheckToken) {
            headers['X-Firebase-AppCheck'] = appCheckToken
        }
    } catch {
        // App Check may be disabled locally
    }
    return headers
}

function mergeInitHeaders(init?: HeadersInit): Record<string, string> {
    const headers: Record<string, string> = {}
    if (!init) return headers
    if (init instanceof Headers) {
        init.forEach((value, key) => {
            headers[key] = value
        })
    } else if (Array.isArray(init)) {
        for (const [key, value] of init) {
            headers[key] = value
        }
    } else {
        Object.assign(headers, init)
    }
    return headers
}

function resolveApiUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path
    }
    const normalized = path.startsWith('/') ? path : `/${path}`
    return `${API_BASE_URL}${normalized}`
}

function queueRefreshAccessToken(): Promise<string> {
    return new Promise((resolve, reject) => {
        addRefreshSubscriber(resolve)
        addRefreshFailureSubscriber(reject)

        if (isRefreshing) {
            return
        }

        isRefreshing = true
        const refreshToken = storage.local.getItem('refresh_token')
        if (!refreshToken) {
            isRefreshing = false
            reject(new Error('No refresh token'))
            return
        }

        api
            .post('/auth/refresh', refreshToken, {
                headers: {
                    'Content-Type': 'text/plain',
                },
            })
            .then((res) => {
                const { access_token, refresh_token } = res.data
                storage.local.setItem('access_token', access_token)
                storage.local.setItem('refresh_token', refresh_token)
                api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
                authTokenSync?.(access_token)
                onRefreshed(access_token)
            })
            .catch(async (refreshError) => {
                await signOutFirebaseAuth()
                storage.local.removeItem('access_token')
                storage.local.removeItem('refresh_token')
                authTokenSync?.(null)
                clearAuthScopedSessionCaches({ clearAllAssistant: true })
                clearLegacyUserProfileCache()
                onRefreshFailed(refreshError)
                window.location.href = '/login'
            })
            .finally(() => {
                isRefreshing = false
            })
    })
}

/**
 * fetch wrapper with auth + App Check headers and silent token refresh on 401.
 * Use for chat/GenUI/search endpoints that need FormData or non-axios response handling.
 */
export async function apiFetch(
    path: string,
    init: RequestInit = {},
    retried = false,
): Promise<Response> {
    const extra = mergeInitHeaders(init.headers)
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
    if (!isFormData && init.body && !extra['Content-Type'] && !extra['content-type']) {
        extra['Content-Type'] = 'application/json'
    }
    if (isFormData) {
        delete extra['Content-Type']
        delete extra['content-type']
    }

    const headers = await getApiRequestHeaders(extra)
    const response = await fetch(resolveApiUrl(path), { ...init, headers })

    if (response.status === 401 && !retried && storage.local.getItem('refresh_token')) {
        try {
            await queueRefreshAccessToken()
            return apiFetch(path, init, true)
        } catch {
            return response
        }
    }

    return response
}

// Request interceptor to add auth token and App Check token
api.interceptors.request.use(
    async (config) => {
        // Let the browser set multipart boundary (default axios JSON Content-Type breaks FormData)
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            delete config.headers['Content-Type']
        }

        const headers = await getApiRequestHeaders()
        Object.assign(config.headers, headers)

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config
        const requestUrl = String(originalRequest?.url || '')
        const isRefreshEndpoint = requestUrl.includes('/auth/refresh')

        // Never try to refresh the refresh request itself (prevents deadlock on 401)
        if (isRefreshEndpoint) {
            return Promise.reject(error)
        }

        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            storage.local.getItem('refresh_token')
        ) {
            originalRequest._retry = true

            // Subscribe BEFORE starting refresh — otherwise a lone 401 (e.g. /users/me on
            // app load) can finish refresh and call onRefreshed with an empty queue, leaving
            // the original request pending forever (infinite "Loading..." until hard refresh).
            return new Promise((resolve, reject) => {
                addRefreshSubscriber((token: string) => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`
                    resolve(api(originalRequest))
                })
                addRefreshFailureSubscriber((refreshError: any) => {
                    reject(refreshError)
                })

                void queueRefreshAccessToken()
            })
        }
        return Promise.reject(error)
    }
)

// Auth API functions
export const authAPI = {
    // Register user (now accepts RegisterUserInput)
    register: async (data: RegisterUserInput) => {
        const response = await api.post('/auth/register', data)
        return response.data
    },

    // Login user (email field accepts email or username)
    login: async (data: { email: string; password: string }) => {
        const response = await api.post('/auth/login', data)
        return response.data
    },

    // Get current user
    getCurrentUser: async () => {
        const response = await api.get('/users/me')
        return response.data
    },

    // Refresh token
    refreshToken: async () => {
        const refreshToken = storage.local.getItem('refresh_token')
        if (!refreshToken) {
            throw new Error('No refresh token available')
        }
        const response = await api.post('/auth/refresh', refreshToken, {
            headers: {
                'Content-Type': 'text/plain'
            }
        })
        return response.data
    },

    // Logout (revokes tokens server-side when refresh token is available)
    logout: async () => {
        const refreshToken = storage.local.getItem('refresh_token')
        const response = await api.post(
            '/auth/logout',
            refreshToken ?? undefined,
            refreshToken
                ? { headers: { 'Content-Type': 'text/plain' } }
                : undefined,
        )
        return response.data
    },

    startImpersonation: async (userId: string) => {
        const response = await api.post(`/auth/impersonate/${userId}`)
        return response.data
    },

    stopImpersonation: async () => {
        const response = await api.post('/auth/impersonate/stop')
        return response.data
    },

    // Firebase phone auth (login)
    phoneLogin: async (data: { id_token: string }) => {
        const response = await api.post('/auth/phone/login', data)
        return response.data
    },

    // Firebase phone auth (complete registration)
    phoneRegister: async (data: {
        id_token: string
        email: string
        first_name: string
        last_name: string
        username: string
        registration_type: 'individual' | 'organization' | 'property_manager'
        user_type?: 'seller' | 'tenant'
        organization_name?: string
        organization_slug?: string
        password: string
        city: string
        preferred_language?: string
        billing_country_code: string
    }) => {
        const response = await api.post('/auth/register/phone', data)
        return response.data
    },

    // Firebase Google auth (login)
    googleLogin: async (data: { id_token: string }) => {
        const response = await api.post('/auth/google/login', data)
        return response.data
    },

    // Firebase Google auth (complete registration)
    googleRegisterFirebase: async (data: {
        id_token: string
        username: string
        registration_type: 'individual' | 'organization' | 'property_manager'
        user_type?: 'seller' | 'tenant'
        organization_name?: string
        organization_slug?: string
        phone?: string
        city: string
        preferred_language?: string
        billing_country_code: string
    }) => {
        const response = await api.post('/auth/register/google/firebase', data)
        return response.data
    },

    // Firebase Facebook auth (login)
    facebookLogin: async (data: { id_token: string }) => {
        const response = await api.post('/auth/facebook/login', data)
        return response.data
    },

    // Firebase Facebook auth (complete registration)
    facebookRegisterFirebase: async (data: {
        id_token: string
        username: string
        registration_type: 'individual' | 'organization' | 'property_manager'
        user_type?: 'seller' | 'tenant'
        organization_name?: string
        organization_slug?: string
        phone?: string
        city: string
        preferred_language?: string
        billing_country_code: string
    }) => {
        const response = await api.post('/auth/register/facebook/firebase', data)
        return response.data
    },
}

// User API functions
export const userAPI = {
    register: async (data: any) => {
        // Forward to authAPI for backward compatibility if needed, or implement directly
        return authAPI.register(data);
    },
    login: async (data: any) => {
        return authAPI.login(data);
    },
    getMe: async () => {
        const response = await api.get('/users/me');
        return response.data;
    },
    getPropertyManagers: async () => {
        const response = await api.get('/users/property-managers');
        return response.data;
    },

    getPublicUser: async (userId: string) => {
        const response = await api.get(`/users/public/${userId}`);
        return response.data;
    },

    updateProfile: async (data: any) => {
        const response = await api.put('/users/me', data)
        return response.data
    },

    sendAadhaarOtp: async (aadhaarNumber: string) => {
        const response = await api.post('/users/me/aadhaar/send-otp', {
            aadhaar_number: aadhaarNumber
        })
        return response.data as {
            message: string
            session_id: string
            mobile_hint?: string | null
        }
    },

    verifyAadhaarOtp: async (sessionId: string, otp: string) => {
        const response = await api.post('/users/me/aadhaar/verify-otp', {
            session_id: sessionId,
            otp
        })
        return response.data as {
            message: string
            aadhaar_verified: boolean
            aadhaar_last_four?: string | null
            aadhaar_verified_at?: string | null
        }
    },

    initiateAadhaarVerification: async () => {
        const response = await api.post('/users/me/aadhaar/initiate')
        return response.data as {
            message: string
            redirect_url: string
            provider_reference_id: string
            expires_in_seconds?: number | null
        }
    },

    getAadhaarStatus: async () => {
        const response = await api.get('/users/me/aadhaar/status')
        return response.data as {
            state?: 'not_started' | 'pending' | 'verified' | 'failed'
            aadhaar_verified: boolean
            aadhaar_last_four?: string | null
            aadhaar_verified_at?: string | null
            provider_reference_id?: string | null
            message?: string | null
        }
    },

    // Get connected providers
    getConnectedProviders: async () => {
        const response = await api.get('/users/me/providers')
        return response.data
    },

    // Link provider
    linkProvider: async (data: { id_token: string; provider: 'google' | 'facebook' | 'phone' }) => {
        const response = await api.post('/users/me/providers/link', data)
        return response.data
    },

    // Unlink provider
    unlinkProvider: async (provider: string) => {
        const response = await api.post(`/users/me/providers/unlink/${provider}`)
        return response.data
    },

    // Get all users (for admin)
    getUsers: async (skip: number = 0, limit: number = 100, search?: string, role?: string, is_active?: boolean) => {
        const response = await api.get('/users/', { params: { skip, limit, search, role, is_active } })
        return response.data
    },

    // Get user by ID (for admin)
    getUser: async (userId: string) => {
        const response = await api.get(`/users/${userId}`)
        return response.data
    },

    // Create user by admin (automatically active and verified)
    createUserByAdmin: async (data: any) => {
        const response = await api.post('/users/', data)
        return response.data
    },

    // Search users (for admin)
    searchUsers: async (q: string, skip: number = 0, limit: number = 100, role?: string, is_active?: boolean) => {
        const response = await api.get('/users/search', { params: { q, skip, limit, role, is_active } })
        return response.data
    },

    // Update user active status
    updateUserStatus: async (userId: string, is_active: boolean) => {
        const response = await api.patch(`/users/${userId}/status`, { is_active })
        return response.data
    },

    // Self-service: upgrade current tenant user to owner role
    upgradeToOwner: async () => {
        const response = await api.post('/users/me/upgrade-to-owner')
        return response.data
    },
}

export const organizationAPI = {
    getOrganizations: async () => {
        const response = await api.get('/organizations')
        return response.data
    },

    getMyOrganizations: async () => {
        const response = await api.get('/organizations/me')
        return response.data
    },

    createOrganization: async (data: { name: string; description?: string; slug: string }) => {
        const response = await api.post('/organizations', data)
        return response.data
    },

    getOrganization: async (id: string) => {
        const response = await api.get(`/organizations/${id}`)
        return response.data
    },

    addUserToOrganization: async (organizationId: string, data: { user_id: string; role: 'ADMIN' | 'MEMBER' }) => {
        const response = await api.post(`/organizations/${organizationId}/users`, data)
        return response.data
    },

    deleteOrganization: async (id: string) => {
        const response = await api.delete(`/organizations/${id}`)
        return response.data
    },

    getOrganizationStats: async (organizationId: string) => {
        const response = await api.get(`/organizations/${organizationId}/stats`)
        return response.data
    },

    getOrganizationUsers: async (organizationId: string) => {
        const response = await api.get(`/organizations/${organizationId}/users`)
        return response.data
    },

    removeUserFromOrganization: async (organizationId: string, userId: string) => {
        const response = await api.delete(`/organizations/${organizationId}/users/${userId}`)
        return response.data
    },

    inviteUserToOrganization: async (organizationId: string, data: {
        email: string;
        role: string;
        personal_message?: string
    }) => {
        const response = await api.post(`/organizations/${organizationId}/invite`, data)
        return response.data
    },

    updateOrganization: async (organizationId: string, data: {
        name?: string;
        description?: string;
        website?: string;
        address?: string;
        phone?: string;
    }) => {
        const response = await api.put(`/organizations/${organizationId}`, data)
        return response.data
    },
}

export const passwordResetAPI = {
    requestPasswordReset: async (email: string) => {
        return api.post('/auth/forgot-password', { email })
    },
    resetPassword: async (token: string, newPassword: string) => {
        return api.post('/auth/reset-password', { token, new_password: newPassword })
    },
    // Backwards compatibility
    requestReset: async (email: string) => {
        return api.post('/auth/forgot-password', { email }).then(res => res.data);
    },
    confirmReset: async (token: string, newPassword: string) => {
        return api.post('/auth/reset-password', { token, new_password: newPassword }).then(res => res.data);
    }
}

export const adminAPI = {
    /** Returns list of users for dropdowns; backend returns { items, total, skip, limit }. */
    getUsers: async () => {
        const response = await api.get('/users/', { params: { skip: 0, limit: 500 } })
        const data = response.data
        return Array.isArray(data) ? data : (data?.items ?? [])
    },
    searchUsers: async (q: string) => {
        if (!q || q.trim().length === 0) return []
        const response = await api.get('/users/search', { params: { q: q.trim(), limit: 20 } })
        return response.data
    },
    getStats: async () => {
        const response = await api.get('/admin/stats')
        return response.data
    },
    firestoreHealth: async () => {
        const response = await api.get('/admin/firestore/health')
        return response.data
    },
    /** Probe currently selected AI Settings provider (admin / super_admin). */
    aiProviderHealth: async () => {
        const response = await api.get('/admin/ai-providers/health')
        return response.data
    },
    getUserBalance: async (userId: string) => {
        const response = await api.get(`/admin/users/${userId}/balance`)
        return response.data
    },
    getUserLedger: async (userId: string, limit: number = 50, offset: number = 0) => {
        const response = await api.get(`/admin/users/${userId}/ledger`, { params: { limit, offset } })
        return response.data
    },
    /** Download usage ledger as CSV (admin). Triggers browser download. */
    exportUsageLedgerCsv: async (days: number = 30, userId?: string) => {
        const params: Record<string, number | string> = { days }
        if (userId) params.user_id = userId
        const response = await api.get('/admin/usage-ledger/export', { params, responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([response.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = response.headers['content-disposition']?.split('filename=')?.[1]?.replace(/^"|"$/g, '') || 'usage_ledger_export.csv'
        a.click()
        URL.revokeObjectURL(url)
    },
    grantUserCredits: async (userId: string, amount: number, description?: string) => {
        const response = await api.post(`/admin/users/${userId}/credits`, { amount, description: description || 'Admin grant' })
        return response.data
    },
    grantOrgCredits: async (orgId: string, amount: number, description?: string) => {
        const response = await api.post(`/admin/organizations/${orgId}/credits`, { amount, description: description || 'Admin grant' })
        return response.data
    },
    getOrgBalanceAdmin: async (orgId: string) => {
        const response = await api.get(`/admin/organizations/${orgId}/balance`)
        return response.data as { success: boolean; org_id: string; org_name: string | null; balance_usd: number; currency: string }
    },
    getOrgLedgerAdmin: async (orgId: string, limit: number = 50, offset: number = 0) => {
        const response = await api.get(`/admin/organizations/${orgId}/ledger`, { params: { limit, offset } })
        return response.data as { success: boolean; org_id: string; entries: any[]; total: number; limit: number; offset: number }
    },
    getUserDetail: async (userId: string, ledgerLimit: number = 20, activityLimit: number = 20) => {
        const response = await api.get(`/admin/users/${userId}/detail`, {
            params: { ledger_limit: ledgerLimit, activity_limit: activityLimit }
        })
        return response.data
    },
    getCronJobs: async (): Promise<{
        success: boolean
        scheduler_enabled: boolean
        in_app_scheduler_enabled: boolean
        env_scheduler_enabled: boolean
        jobs: Array<{
            key: string
            description: string
            enabled: boolean
            hour_utc: number
            minute_utc: number
            last_run: {
                id: string
                status: string
                trigger: string
                started_at: string | null
                finished_at: string | null
                records_processed: number | null
                error_message: string | null
            } | null
        }>
    }> => {
        const response = await api.get('/admin/cron-jobs')
        return response.data
    },
    updateCronJobs: async (payload: {
        scheduler_enabled?: boolean
        jobs?: Record<string, { enabled?: boolean; hour_utc?: number; minute_utc?: number }>
    }) => {
        const response = await api.put('/admin/cron-jobs', payload)
        return response.data
    },
    runCronJob: async (jobKey: string) => {
        const response = await api.post(`/admin/cron-jobs/${jobKey}/run`)
        return response.data
    },
}

/** Current-user billing and usage (authenticated user). */
export const billingAPI = {
    getUsage: async (days: number = 30) => {
        const response = await api.get('/billing/usage', { params: { days } })
        return response.data
    },
    getBalance: async (): Promise<WalletBalance> => {
        const response = await api.get('/billing/balance')
        return response.data as WalletBalance
    },
    getLedger: async (limit: number = 50, offset: number = 0) => {
        const response = await api.get('/billing/ledger', { params: { limit, offset } })
        return response.data
    },
    getLimitStatus: async (): Promise<{
        monthly_cap_usd: number | null
        used_this_period_usd: number
        period_start: string | null
        period_end: string | null
        over_cap: boolean
    }> => {
        const response = await api.get('/billing/limit-status')
        return response.data
    },
    getOrgBalance: async (orgId: string): Promise<WalletBalance & { org_id: string }> => {
        const response = await api.get(`/billing/org/${orgId}/balance`)
        return response.data as WalletBalance & { org_id: string }
    },
    getOrgUsage: async (orgId: string, days: number = 30) => {
        const response = await api.get(`/billing/org/${orgId}/usage`, { params: { days } })
        return response.data
    },
    getOrgLedger: async (orgId: string, limit: number = 50, offset: number = 0) => {
        const response = await api.get(`/billing/org/${orgId}/ledger`, { params: { limit, offset } })
        return response.data
    },
}

/** Token usage metrics (super_admin/admin only). Data comes from DB or Firestore per options. */
export const metricsAPI = {
    getTokenUsageSummary: async (days: number = 7) => {
        const response = await api.get('/metrics/token-usage/summary', { params: { days } })
        return response.data
    },
    getTokenUsageRecent: async (limit: number = 50) => {
        const response = await api.get('/metrics/token-usage/recent', { params: { limit } })
        return response.data
    },
    getTokenUsageDaily: async (days: number = 30) => {
        const response = await api.get('/metrics/token-usage/daily', { params: { days } })
        return response.data
    },
    getTokenUsageByFormat: async (days: number = 7) => {
        const response = await api.get('/metrics/token-usage/by-format', { params: { days } })
        return response.data
    },
    getTokenUsageByUser: async (userId: string, days: number = 30) => {
        const response = await api.get(`/metrics/token-usage/by-user/${userId}`, { params: { days } })
        return response.data
    },
    getTokenUsageByOrg: async (orgId: string, days: number = 30) => {
        const response = await api.get(`/metrics/token-usage/by-org/${orgId}`, { params: { days } })
        return response.data
    },
    getTokenUsageHealth: async () => {
        const response = await api.get('/metrics/token-usage/health')
        return response.data
    },
    /** Download token usage as CSV (admin). Triggers browser download. */
    exportTokenUsageCsv: async (days: number = 30, userId?: string, endpointContext?: string) => {
        const params: Record<string, number | string> = { days }
        if (userId) params.user_id = userId
        if (endpointContext) params.endpoint_context = endpointContext
        const response = await api.get('/metrics/token-usage/export', { params, responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([response.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = response.headers['content-disposition']?.split('filename=')?.[1]?.replace(/^"|"$/g, '') || 'token_usage_export.csv'
        a.click()
        URL.revokeObjectURL(url)
    },
    // API request usage (super_admin/admin only)
    getApiUsageSummary: async (days: number = 7) => {
        const response = await api.get('/metrics/api-usage/summary', { params: { days } })
        return response.data
    },
    getApiUsageRecent: async (limit: number = 50) => {
        const response = await api.get('/metrics/api-usage/recent', { params: { limit } })
        return response.data
    },
    getApiUsageByUser: async (userId: string, days: number = 30) => {
        const response = await api.get(`/metrics/api-usage/by-user/${userId}`, { params: { days } })
        return response.data
    },
    getApiUsageByOrganization: async (organizationId: string, days: number = 30) => {
        const response = await api.get(`/metrics/api-usage/by-organization/${organizationId}`, { params: { days } })
        return response.data
    },
    getApiUsageDaily: async (days: number = 30) => {
        const response = await api.get('/metrics/api-usage/daily', { params: { days } })
        return response.data
    },
    getGoogleMapsSummary: async () => {
        const response = await api.get('/metrics/google-maps/summary')
        return response.data
    },
    getGoogleMapsRecent: async (limit: number = 40) => {
        const response = await api.get('/metrics/google-maps/recent', { params: { limit } })
        return response.data
    },
    reportGoogleMapsEvent: async (sku: string, context: string, country?: string) => {
        const response = await api.post('/metrics/google-maps/events', { sku, context, country })
        return response.data
    },
}

/** Redis inspection (super_admin only). */
export const redisInsightAPI = {
    getOverview: async () => {
        const response = await api.get('/redis-insight/overview')
        return response.data
    },
    scan: async (params: { cursor?: number; match?: string; count?: number }) => {
        const response = await api.get('/redis-insight/scan', { params })
        return response.data
    },
    getKeyDetails: async (key: string) => {
        const response = await api.post('/redis-insight/key-details', { key })
        return response.data
    },
    getKeyPrefixReference: async () => {
        const response = await api.get('/redis-insight/key-prefix-reference')
        return response.data
    },
}

// RBAC API functions
export const rbacAPI = {
    // Role Management
    getRoles: async () => {
        const response = await api.get('/rbac/roles')
        return response.data
    },

    getRole: async (roleId: number) => {
        const response = await api.get(`/rbac/roles/${roleId}`)
        return response.data
    },

    createRole: async (data: { name: string; description?: string; is_system_role?: boolean }) => {
        const response = await api.post('/rbac/roles', data)
        return response.data
    },

    updateRole: async (roleId: number, data: { name?: string; description?: string; is_active?: boolean }) => {
        const response = await api.put(`/rbac/roles/${roleId}`, data)
        return response.data
    },

    deleteRole: async (roleId: number) => {
        const response = await api.delete(`/rbac/roles/${roleId}`)
        return response.data
    },

    // Permission Management
    getPermissions: async (resource?: string) => {
        const params = resource ? { resource } : {}
        const response = await api.get('/rbac/permissions', { params })
        return response.data
    },

    getPermissionResources: async () => {
        const response = await api.get('/rbac/permissions/resources')
        return response.data
    },

    // User Role Management
    getUserRoles: async (userId: string, organizationId?: string) => {
        const params = organizationId ? { organization_id: organizationId } : {}
        const response = await api.get(`/rbac/users/${userId}/roles`, { params })
        return response.data
    },

    getUserAssignments: async (userId: string) => {
        const response = await api.get(`/rbac/users/${userId}/assignments`)
        return response.data
    },

    getAllUserRoles: async (skip: number = 0, limit: number = 100, search?: string, role?: string, is_active?: boolean) => {
        const response = await api.get('/rbac/users/roles', { params: { skip, limit, search, role, is_active } })
        return response.data
    },

    assignRoleToUser: async (userId: string, roleId: number, organizationId?: string) => {
        const response = await api.post(`/rbac/users/${userId}/roles`, {
            role_id: roleId,
            organization_id: organizationId
        })
        return response.data
    },

    removeRoleFromUser: async (userId: string, roleId: number, organizationId?: string) => {
        const params = organizationId ? { organization_id: organizationId } : {}
        const response = await api.delete(`/rbac/users/${userId}/roles/${roleId}`, { params })
        return response.data
    },

    // Permission Checking
    checkPermission: async (permission: string, organizationId?: string) => {
        const response = await api.post('/rbac/check-permission', {
            permission,
            organization_id: organizationId
        })
        return response.data
    },

    getUserPermissions: async (userId: string, organizationId?: string) => {
        const params = organizationId ? { organization_id: organizationId } : {}
        const response = await api.get(`/rbac/users/${userId}/permissions`, { params })
        return response.data
    },

    // Role-Permission Assignment
    assignPermissionToRole: async (roleId: number, permissionId: number) => {
        const response = await api.post(`/rbac/roles/${roleId}/permissions/${permissionId}`)
        return response.data
    },

    removePermissionFromRole: async (roleId: number, permissionId: number) => {
        const response = await api.delete(`/rbac/roles/${roleId}/permissions/${permissionId}`)
        return response.data
    },

    // Get roles that have a specific permission
    getRolesWithPermission: async (permissionId: number) => {
        const response = await api.get(`/rbac/permissions/${permissionId}/roles`)
        return response.data
    }
}

export const checkUsername = async (username: string) => {
    const response = await api.get(`/users/check-username?username=${encodeURIComponent(username)}`);
    return response.data;
};

// Public API (no auth required for these endpoints)
export const publicAPI = {
    getPublicConfig: async (options?: { force?: boolean }): Promise<{
        firebase_auth_enabled: boolean;
        firebase_phone_auth_enabled: boolean;
        google_auth_enabled: boolean;
        facebook_auth_enabled: boolean;
        voice_search_enabled: boolean;
        ai_chat_enabled: boolean;
        property_comparison_enabled: boolean;
        aadhaar_verification_enabled: boolean;
        credits_only_mode: boolean;
        international_listings_enabled: boolean;
        location_provider: 'database' | 'openstreetmap' | 'google';
        google_maps_premium_enabled?: boolean;
        maps_enabled?: boolean;
        maps_search_mode?: 'off' | 'openstreetmap' | 'google';
        maps_posting_mode?: 'off' | 'openstreetmap' | 'google';
        google_maps_map_price_usd?: number;
        google_maps_lookup_price_usd?: number;
        google_maps_map_price_inr?: number;
        google_maps_lookup_price_inr?: number;
        google_maps_block_when_unpaid?: boolean;
        geo_search_enabled: boolean;
        maintenance_mode: boolean;
        maintenance_message: string | null;
        max_property_media_limit?: number;
        max_scraper_video_limit?: number;
        max_video_upload_mb?: number;
        max_image_upload_mb?: number;
    }> => {
        const response = await api.get('/public/config', {
            timeout: 12_000,
            params: options?.force ? { _t: Date.now() } : undefined,
            headers: options?.force ? { 'Cache-Control': 'no-cache' } : undefined,
        });
        return response.data;
    },
};

// Options API
export const optionsAPI = {
    // Get all options
    getAllOptions: async () => {
        const response = await api.get('/options/')
        return response.data
    },

    // Get a specific option by name
    getOption: async (optionName: string) => {
        const response = await api.get(`/options/${optionName}`)
        return response.data
    },

    // Create a new option
    createOption: async (optionName: string, optionValue: any) => {
        const response = await api.post('/options/', {
            option_name: optionName,
            option_value: optionValue
        })
        return response.data
    },

    // Update an option (upsert)
    updateOption: async (optionName: string, optionValue: any) => {
        const response = await api.patch('/options/', {
            option_name: optionName,
            option_value: optionValue
        })
        return response.data
    },

    // Delete an option
    deleteOption: async (optionName: string) => {
        const response = await api.delete(`/options/${optionName}`)
        return response.data
    },

    // Update the low-balance alert threshold (super_admin only, enforced by backend)
    updateLowBalanceThreshold: (currency: 'usd' | 'inr', optionValue: number) =>
        api.put(`/options/low-balance-threshold/${currency}`, null, {
            params: { option_value: optionValue }
        }),

    getLlmPricingSchema: async (): Promise<{
        providers: string[]
        fields: string[]
        provider_fields?: Record<string, string[]>
        provider_currencies?: Record<string, string>
        provider_notes?: Record<string, string>
    }> => {
        const response = await api.get<{
            providers: string[]
            fields?: string[]
            provider_fields?: Record<string, string[]>
            provider_currencies?: Record<string, string>
            provider_notes?: Record<string, string>
        }>('/options/llm-pricing-schema')
        return {
            providers: response.data.providers,
            fields: response.data.fields ?? [],
            provider_fields: response.data.provider_fields,
            provider_currencies: response.data.provider_currencies,
            provider_notes: response.data.provider_notes,
        }
    },

    getBillingMarginSchema: async (): Promise<{
        providers: string[]
        env_vars: { margin_percent: string; priority: string }
        default_priority: string
        option_key: string
    }> => {
        const response = await api.get('/options/billing-margin-schema')
        return response.data
    },

    getBillingMarginResolved: async (): Promise<{
        source_summary: string
        env_margin_percent: number | null
        options: {
            global_margin_percent?: number
            priority?: string
            per_provider?: Record<string, number>
        }
        resolved: Record<string, { percent: number; source: string }>
    }> => {
        const response = await api.get('/options/billing-margin-resolved')
        return response.data
    },

    getFxSettingsSchema: async (): Promise<{
        option_key: string
        config: Record<string, unknown>
        defaults: Record<string, unknown>
        providers: string[]
        provider_labels: Record<string, string>
        resolved_rate: { inr_per_usd: number; as_of: string; source: string }
    }> => {
        const response = await api.get('/options/fx-settings-schema')
        return response.data
    },

    getFeatureLimitsSchema: async (): Promise<{ limits: any[] }> => {
        const response = await api.get<{ limits: any[] }>('/options/feature-limits-schema')
        return response.data
    },

    getProtectedKeys: async (): Promise<{ keys: string[] }> => {
        const response = await api.get<{ keys: string[] }>('/options/protected-keys')
        return response.data
    },

    discoverModels: async (base_url: string, api_key: string): Promise<{ models: Array<{ id: string; name: string }> }> => {
        const response = await api.post('/options/discover-models', { base_url, api_key })
        return response.data
    },

    sendSmtpTest: async (to_email: string): Promise<{ success: boolean; source_summary: string }> => {
        const response = await api.post<{ success: boolean; source_summary: string }>('/options/smtp-test', { to_email })
        return response.data
    },
}

export const analyticsAPI = {
    trackEvent: async (data: { property_id: string; event_type: string; page_path?: string; location?: string; metadata?: any }) => {
        const response = await api.post('/analytics/track', data);
        return response.data;
    },
    getStats: async (propertyId?: string) => {
        const url = propertyId
            ? `/analytics/stats/property/${propertyId}`
            : `/analytics/stats/manager`;
        const response = await api.get(url);
        return response.data;
    },
    getTopProperties: async (limit: number = 10) => {
        const response = await api.get('/analytics/top-properties', { params: { limit } });
        return response.data;
    }
};

export const platformAnalyticsAPI = {
    getStats: async (days: number = 30) => {
        const response = await api.get('/platform/stats', { params: { days } });
        return response.data;
    },
    getRevenue: async (days: number = 30) => {
        const response = await api.get('/platform/revenue', { params: { days } });
        return response.data;
    },
};

export const propertyAPI = {
    listProperties: async (params: {
        property_type?: string;
        property_type_in?: string;
        price_type?: string;
        min_price?: number;
        max_price?: number;
        bedrooms?: number;
        bedrooms_in?: string;
        city?: string;
        locality?: string;
        search?: string;
        area_min?: number;
        area_max?: number;
        area_type?: string;
        bathrooms?: number;
        furnishing?: string;
        sub_property_type?: string;
        has_photos?: boolean;
        posted_since?: string;
        posted_by_role?: string;
        price_negotiable?: boolean;
        facing?: string;
        floor_number?: number;
        parking_min?: number;
        possession_status?: string;
        amenities?: string;
        connectivity?: string;
        highlights?: string;
        owner_id?: string;
        organization_id?: string;
        manager_id?: string;
        portfolio_user_id?: string;
        status?: string;
        skip?: number;
        limit?: number;
        sort_by?: string;
        order?: string;
    }) => {
        const response = await api.get('/properties/', { params });
        return response.data as {
            success: boolean;
            total: number;
            skip: number;
            limit: number;
            count: number;
            properties: import('../types').Property[];
        };
    },

    getProperty: async (id: string) => {
        const response = await api.get(`/properties/${id}`);
        return response.data;
    },

    createProperty: async (data: any) => {
        const response = await api.post('/properties/', data);
        return response.data;
    },

    listPropertyDrafts: async () => {
        const response = await api.get('/properties/drafts');
        return response.data as {
            success: boolean;
            drafts: Array<{
                id: string;
                title?: string;
                property_type?: string;
                updated_at?: string | null;
                genui_session_id?: string | null;
                preview?: Record<string, unknown>;
            }>;
        };
    },

    createOrReusePropertyDraft: async (body?: {
        reuse_latest?: boolean;
        genui_session_id?: string | null;
        property_type?: string;
        intent?: string;
        title?: string;
    }) => {
        const response = await api.post('/properties/draft', body ?? { reuse_latest: true });
        return response.data as {
            success: boolean;
            draft_id: string;
            property: Record<string, unknown>;
            reused?: boolean;
        };
    },

    patchPropertyDraft: async (draftId: string, body: Record<string, unknown>) => {
        const response = await api.patch(`/properties/${draftId}/draft`, body);
        return response.data;
    },

    discardPropertyDraft: async (draftId: string) => {
        const response = await api.delete(`/properties/${draftId}/draft`);
        return response.data;
    },

    publishPropertyDraft: async (draftId: string, formData: FormData) => {
        const response = await api.post(`/properties/${draftId}/publish`, formData);
        return response.data;
    },

    updateProperty: async (id: string, data: any) => {
        const response = await api.put(`/properties/${id}`, data);
        return response.data;
    },

    deleteProperty: async (id: string) => {
        const response = await api.delete(`/properties/${id}`);
        return response.data;
    },

    deletePropertyImage: async (propertyId: string, imageId: string) => {
        const response = await api.delete(`/properties/${propertyId}/images/${imageId}`);
        return response.data;
    },

    setPropertyImageFeatured: async (propertyId: string, imageId: string) => {
        const response = await api.put(`/properties/${propertyId}/images/${imageId}/featured`);
        return response.data;
    },

    getManagerStats: async () => {
        const response = await api.get('/properties/manager/stats');
        return response.data;
    },

    importProperties: async (file: File, options?: { target_user_id?: string; organization_id?: string }) => {
        const formData = new FormData()
        formData.append('file', file)
        if (options?.target_user_id) {
            formData.append('target_user_id', options.target_user_id)
        }
        if (options?.organization_id) {
            formData.append('organization_id', options.organization_id)
        }
        const response = await api.post('/properties/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return response.data
    },

    /** Excel with `listing_paragraph` per row; server uses LLM to extract fields then saves like column import. */
    importParagraphProperties: async (
        file: File,
        options?: { target_user_id?: string; organization_id?: string; llm_provider?: string }
    ) => {
        const formData = new FormData()
        formData.append('file', file)
        if (options?.target_user_id) {
            formData.append('target_user_id', options.target_user_id)
        }
        if (options?.organization_id) {
            formData.append('organization_id', options.organization_id)
        }
        if (options?.llm_provider) {
            formData.append('llm_provider', options.llm_provider)
        }
        const response = await api.post('/properties/bulk/import/paragraph', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return response.data
    },

    previewBulkImport: async (
        file: File,
        options?: { import_format?: 'columns' | 'paragraph'; target_user_id?: string; organization_id?: string }
    ) => {
        const formData = new FormData()
        formData.append('file', file)
        if (options?.import_format) {
            formData.append('import_format', options.import_format)
        }
        if (options?.target_user_id) {
            formData.append('target_user_id', options.target_user_id)
        }
        if (options?.organization_id) {
            formData.append('organization_id', options.organization_id)
        }
        const response = await api.post('/properties/import/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return response.data
    },

    scrapePropertyUrls: async (payload: {
        urls: string[];
        organization_id?: string;
        target_user_id?: string;
        llm_provider?: string;
    }) => {
        const response = await api.post('/properties/import/url-scrape', payload)
        return response.data
    },

    confirmBulkImportBatch: async (payload: {
        publish_mode: 'publish_ready' | 'publish_all' | 'save_all_drafts';
        target_user_id?: string;
        organization_id?: string;
        items: Array<{
            temp_id?: string;
            row_index?: number;
            property_type?: string;
            title: string;
            description?: string;
            price?: number | null;
            price_type?: string;
            price_negotiable?: boolean;
            bedrooms?: number | null;
            bathrooms?: number | null;
            area_sqft?: number | string | null;
            furnishing?: string | null;
            parking_spaces?: number | null;
            locality?: string | null;
            city?: string | null;
            state?: string | null;
            pincode?: string | null;
            street_address?: string | null;
            lat?: number | null;
            lng?: number | null;
            amenities?: string[];
            image_urls?: string[];
            video_urls?: string[];
            status?: string;
            [key: string]: any;
        }>;
    }) => {
        const response = await api.post('/properties/import/confirm-batch', payload)
        return response.data
    },

    genuiEnrichBulkProperties: async (payload: {
        items: Array<{
            temp_id: string;
            property_type?: string;
            title?: string;
            description?: string;
            price?: number | null;
            price_type?: string;
            bedrooms?: number | null;
            bathrooms?: number | null;
            area_sqft?: number | null;
            furnishing?: string | null;
            parking_spaces?: number | null;
            facing?: string | null;
            floor_number?: number | null;
            total_floors?: number | null;
            balconies?: number | null;
            maintenance?: number | null;
            deposit?: number | null;
            locality?: string | null;
            city?: string | null;
            street_address?: string | null;
            project_name?: string | null;
            society_name?: string | null;
            age_of_property?: string | null;
            possession_status?: string | null;
            amenities?: string[];
        }>;
        organization_id?: string;
    }) => {
        const response = await api.post('/properties/import/genui-enrich', payload)
        return response.data as {
            success: boolean;
            enriched_count: number;
            items: Array<{
                temp_id: string;
                title: string;
                description: string;
                amenities: string[];
                highlights: string[];
                facing?: string | null;
                furnishing?: string | null;
                parking_spaces?: number | null;
                floor_number?: number | null;
                total_floors?: number | null;
                balconies?: number | null;
                maintenance?: number | null;
                deposit?: number | null;
                project_name?: string | null;
                society_name?: string | null;
                age_of_property?: string | null;
                possession_status?: string | null;
                filled_fields: string[];
                rewritten_fields: string[];
            }>;
        }
    },

    getImportJobStatus: async (jobId: string) => {
        const response = await api.get(`/properties/import/jobs/${jobId}`)
        return response.data
    },

    listImportJobs: async (limit: number = 20) => {
        const response = await api.get('/properties/import/jobs', { params: { limit } })
        return response.data
    },

    downloadImportTemplate: async () => {
        const response = await api.get('/properties/import/template', { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([response.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = response.headers['content-disposition']?.split('filename=')?.[1]?.replace(/^"|"$/g, '') || 'property_import_template.xlsx'
        a.click()
        URL.revokeObjectURL(url)
    },

    downloadParagraphImportTemplate: async () => {
        const response = await api.get('/properties/import/template-paragraph', { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([response.data]))
        const a = document.createElement('a')
        a.href = url
        a.download =
            response.headers['content-disposition']?.split('filename=')?.[1]?.replace(/^"|"$/g, '') ||
            'property_paragraph_import_template.xlsx'
        a.click()
        URL.revokeObjectURL(url)
    },

    /** Local images → public URLs for Excel `image_urls` (S3 or configured public API base). */
    uploadBulkImportStagingImages: async (files: File[]) => {
        const formData = new FormData()
        files.forEach((f) => formData.append('files', f))
        const response = await api.post('/properties/import/staging-images', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return response.data
    },
}

export const tenantAPI = {
    createInquiry: async (propertyId: string, message: string, interestType: string = 'rent', page_path?: string, location?: string) => {
        const response = await api.post('/tenants/inquire', {
            property_id: propertyId,
            message: message,
            interest_type: interestType,
            page_path: page_path,
            location: location
        });
        return response.data;
    },
    listTenants: async (params?: any) => {
        const response = await api.get('/tenants/', { params });
        return response.data;
    },
    updateStatus: async (id: string, data: any) => {
        const response = await api.patch(`/tenants/${id}/status`, data);
        return response.data;
    },
    delete: async (id: string) => {
        const response = await api.delete(`/tenants/${id}`);
        return response.data;
    },
    addManualTenant: async (data: {
        email: string;
        property_id: string;
        rent_amount: number;
        lease_start_date: string;
        lease_end_date: string;
    }) => {
        const response = await api.post('/tenants/', data);
        return response.data;
    },
    uploadLease: async (id: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/tenants/${id}/upload-lease`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        return response.data;
    },
    uploadSignedLease: async (id: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/tenants/${id}/upload-signed-lease`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        return response.data;
    }
};

export const brokerageAPI = {
    inviteManager: async (data: any) => {
        const response = await api.post('/brokerage/invite', data);
        return response.data;
    },
    getInvitations: async () => {
        const response = await api.get('/brokerage/invitations');
        return response.data;
    },
    updateInvitationStatus: async (id: string, data: any) => {
        const response = await api.patch(`/brokerage/invitations/${id}`, data);
        return response.data;
    },
    getManagedProperties: async () => {
        const response = await api.get('/brokerage/managed-properties');
        return response.data;
    }
};

export const operationsAPI = {
    createMaintenanceRequest: async (data: any) => {
        const response = await api.post('/operations/maintenance', data);
        return response.data;
    },
    listMaintenanceRequests: async () => {
        const response = await api.get('/operations/maintenance');
        return response.data;
    },
    updateMaintenanceRequest: async (id: string, data: any) => {
        const response = await api.patch(`/operations/maintenance/${id}`, data);
        return response.data;
    },
    createAlert: async (data: any) => {
        const response = await api.post('/operations/alerts', data);
        return response.data;
    },
    listAlerts: async (propertyId: string) => {
        const response = await api.get(`/operations/alerts/${propertyId}`);
        return response.data;
    }
};

export const notificationAPI = {
    getNotifications: async (skip: number = 0, limit: number = 50) => {
        const response = await api.get('/notifications/', { params: { skip, limit } })
        return response.data
    },
    markAsRead: async (id: number) => {
        const response = await api.post(`/notifications/${id}/read`)
        return response.data
    },
    deleteNotification: async (id: number) => {
        const response = await api.delete(`/notifications/${id}`)
        return response.data
    },
    getUnreadCount: async () => {
        const response = await api.get('/notifications/unread-count')
        return response.data
    },
    getVapidPublicKey: async () => {
        const response = await api.get('/notifications/push-subscriptions/vapid-public-key')
        return response.data
    },
    createPushSubscription: async (subscription: any) => {
        const response = await api.post('/notifications/push-subscriptions', { subscription })
        return response.data
    },
    deletePushSubscription: async (endpoint: string) => {
        const response = await api.delete('/notifications/push-subscriptions', { data: { endpoint } })
        return response.data
    },
    /** Refresh Redis presence TTL while the tab is open (multi-worker online detection). */
    presenceHeartbeat: async () => {
        const response = await api.post('/notifications/presence/heartbeat')
        return response.data as { ok: boolean; online: boolean }
    },
};

export const favouriteAPI = {
    getFavourites: async () => {
        const response = await api.get('/favourites/');
        return response.data;
    },
    addFavourite: async (propertyId: string) => {
        const response = await api.post('/favourites/', { property_id: propertyId });
        return response.data;
    },
    removeFavourite: async (propertyId: string) => {
        const response = await api.delete(`/favourites/${propertyId}`);
        return response.data;
    },
};

export type PropertyReviewItem = {
    id: string;
    rating: number;
    comment?: string | null;
    reviewer_name: string;
    reviewer_initials: string;
    is_verified: boolean;
    is_guest: boolean;
    helpful_count: number;
    helpful: boolean;
    reported: boolean;
    created_at: string;
    updated_at: string;
    is_mine: boolean;
};

export type PropertyReviewsPayload = {
    average: number;
    average_rating: number;
    count: number;
    public_review_count: number;
    verified_count: number;
    verified_review_count: number;
    can_review: boolean;
    cannot_review_reason: 'login' | 'owner' | 'closed' | null;
    my_review: PropertyReviewItem | null;
    reviews: PropertyReviewItem[];
    has_more: boolean;
    skip: number;
    limit: number;
};

export type PropertyReviewSummary = {
    average: number;
    average_rating: number;
    count: number;
    public_review_count: number;
    verified_count: number;
    verified_review_count: number;
};

function normalizeReviewSummary(data: Partial<PropertyReviewSummary> = {}): PropertyReviewSummary {
    const average = Number(data.average_rating ?? data.average ?? 0)
    const count = Number(data.public_review_count ?? data.count ?? 0)
    const verified_count = Number(data.verified_review_count ?? data.verified_count ?? 0)
    return { average, average_rating: average, count, public_review_count: count, verified_count, verified_review_count: verified_count }
}

function normalizeReviewsPayload(data: PropertyReviewsPayload): PropertyReviewsPayload {
    return { ...data, ...normalizeReviewSummary(data) }
}

const reviewSummaryCache = new Map<string, PropertyReviewSummary>()
const reviewSummaryInflight = new Map<string, Promise<PropertyReviewSummary>>()

export const propertyReviewsAPI = {
    ensureGuestSession: async () => {
        const response = await api.get('/guest-review-session', { withCredentials: true })
        return response.data as { guest_session_id: string }
    },
    summary: async (propertyId: string) => {
        const cached = reviewSummaryCache.get(propertyId)
        if (cached) return cached
        const pending = reviewSummaryInflight.get(propertyId)
        if (pending) return pending
        const request = api.get(`/properties/${propertyId}/reviews/summary`).then((response) => {
            const data = normalizeReviewSummary(response.data as PropertyReviewSummary)
            reviewSummaryCache.set(propertyId, data)
            reviewSummaryInflight.delete(propertyId)
            return data
        }).catch((error) => {
            reviewSummaryInflight.delete(propertyId)
            throw error
        })
        reviewSummaryInflight.set(propertyId, request)
        return request
    },
    rememberSummary: (propertyId: string, data: Partial<PropertyReviewSummary>) => {
        reviewSummaryCache.set(propertyId, normalizeReviewSummary(data))
    },
    list: async (propertyId: string, params?: { skip?: number; limit?: number; guest_session_id?: string }) => {
        const response = await api.get(`/properties/${propertyId}/reviews`, { params, withCredentials: true })
        return normalizeReviewsPayload(response.data as PropertyReviewsPayload)
    },
    submit: async (propertyId: string, data: { rating: number; comment?: string; guest_session_id?: string; guest_name?: string; guest_email?: string }) => {
        const response = await api.post(`/properties/${propertyId}/reviews`, data, { withCredentials: true })
        return normalizeReviewsPayload(response.data as PropertyReviewsPayload)
    },
    helpful: async (propertyId: string, reviewId: string) => {
        const response = await api.post(`/properties/${propertyId}/reviews/${reviewId}/helpful`)
        return response.data as { id: string; helpful_count: number; helpful: boolean; reported: boolean }
    },
    report: async (propertyId: string, reviewId: string) => {
        const response = await api.post(`/properties/${propertyId}/reviews/${reviewId}/report`)
        return response.data as { id: string; helpful_count: number; helpful: boolean; reported: boolean }
    },
    verify: async (propertyId: string, reviewId: string) => {
        const response = await api.post(`/properties/${propertyId}/reviews/${reviewId}/verify`)
        return normalizeReviewsPayload(response.data as PropertyReviewsPayload)
    },
    adminList: async (params?: { q?: string; property_id?: string; limit?: number; offset?: number }) => {
        const response = await api.get('/admin/property-reviews', { params })
        return response.data as { items: AdminPropertyReview[]; total: number; limit: number; offset: number }
    },
    adminDelete: async (reviewId: string) => {
        await api.delete(`/admin/property-reviews/${reviewId}`)
    },
}

export type AdminPropertyReview = {
    id: string;
    property_id: string;
    property_title: string;
    rating: number;
    comment?: string | null;
    reviewer_name: string;
    reviewer_email?: string | null;
    is_guest: boolean;
    is_verified: boolean;
    created_at: string;
};

export const supportAPI = {
    createRequest: async (data: { name: string; email: string; subject: string; message: string }) => {
        const response = await api.post('/support/request', data);
        return response.data;
    },
    getMyRequests: async () => {
        const response = await api.get('/support/my-requests');
        return response.data;
    },
    getAllRequests: async () => {
        const response = await api.get('/support/all-requests');
        return response.data;
    },
    replyToRequest: async (id: string, data: { message: string, status?: string }) => {
        const response = await api.post(`/support/${id}/reply`, data);
        return response.data;
    }
};

export type FeedbackSubmitPayload = {
    categories: string[];
    message: string;
    rating: number;
    page_url?: string;
    route?: string;
    browser?: string;
    device?: string;
    os?: string;
    website?: string;
    form_started_at_ms?: number;
    screenshot?: File;
    guest_name?: string;
    guest_email?: string;
};

export type FeedbackItem = {
    id: string;
    public_id: string;
    full_name: string;
    email: string;
    email_verified: boolean;
    rating: number;
    category: string;
    subcategory?: string | null;
    message: string;
    expected_behavior?: string | null;
    page_url?: string | null;
    route?: string | null;
    browser?: string | null;
    device?: string | null;
    os?: string | null;
    status: string;
    admin_notes?: string | null;
    assigned_team?: string | null;
    attachments: Array<{ id: string; file_url: string; file_name: string; mime_type: string; file_size: number }>;
    created_at: string;
    updated_at: string;
};

export const feedbackAPI = {
    submit: async (data: FeedbackSubmitPayload) => {
        const form = new FormData();
        form.append('categories', data.categories.join(','));
        form.append('message', data.message);
        form.append('rating', String(data.rating));
        if (data.page_url) form.append('page_url', data.page_url);
        if (data.route) form.append('route', data.route);
        if (data.browser) form.append('browser', data.browser);
        if (data.device) form.append('device', data.device);
        if (data.os) form.append('os', data.os);
        if (data.website) form.append('website', data.website);
        if (data.form_started_at_ms != null) form.append('form_started_at_ms', String(data.form_started_at_ms));
        if (data.guest_name) form.append('guest_name', data.guest_name);
        if (data.guest_email) form.append('guest_email', data.guest_email);
        if (data.screenshot) form.append('screenshot', data.screenshot);
        const response = await api.post('/feedback', form);
        return response.data as { public_id: string; status: string; created_at: string; message: string };
    },
    adminList: async (params?: { status?: string; q?: string; limit?: number; offset?: number }) => {
        const response = await api.get('/feedback/admin/list', { params });
        return response.data as { items: FeedbackItem[]; total: number; limit: number; offset: number };
    },
    adminGet: async (id: string) => {
        const response = await api.get(`/feedback/admin/${id}`);
        return response.data as FeedbackItem;
    },
    adminUpdate: async (id: string, data: { status?: string; admin_notes?: string; assigned_team?: string }) => {
        const response = await api.patch(`/feedback/admin/${id}`, data);
        return response.data as FeedbackItem;
    },
    adminAttachmentUrl: (attachmentId: string) => `/feedback/admin/attachments/${attachmentId}`,
    adminGetAttachmentBlob: async (attachmentId: string) => {
        const response = await api.get(`/feedback/admin/attachments/${attachmentId}`, { responseType: 'blob' });
        return URL.createObjectURL(response.data);
    },
};

export const locationAPI = {
    searchAreas: async (query: string, limit: number = 10, countryCode?: string, countryName?: string) => {
        const response = await api.get('/locations/search', {
            params: {
                query,
                limit,
                ...(countryCode ? { country_code: countryCode } : {}),
                ...(countryName ? { country: countryName } : {}),
            },
        })
        return response.data
    },
    reverseGeocode: async (
        lat: number,
        lon: number,
        sessionKey?: string,
        options?: { raw?: boolean },
    ) => {
        const response = await api.get('/locations/reverse-geocode', {
            params: {
                lat,
                lon,
                ...(sessionKey ? { session_key: sessionKey } : {}),
                ...(options?.raw ? { raw: true } : {}),
            },
        })
        return response.data as {
            city: string
            state: string
            country: string
            countryCode: string
            lat: number
            lon: number
            area?: string
            pincode?: string
            source?: string
            catalog_enabled?: boolean
            city_id?: number
            state_id?: number
            country_id?: number
            locality_id?: number
            disabled_reason?: string
        } | null
    },
    recordBrowserPosition: async (
        lat: number,
        lon: number,
        accuracy?: number,
        sessionKey?: string,
    ) => {
        const response = await api.post('/locations/browser-position', {
            lat,
            lon,
            ...(accuracy != null ? { accuracy } : {}),
            ...(sessionKey ? { session_key: sessionKey } : {}),
        })
        return response.data as {
            city: string
            state: string
            country: string
            countryCode: string
            lat: number
            lon: number
            area?: string
            pincode?: string
            source?: string
            catalog_enabled?: boolean
            city_id?: number
            state_id?: number
            country_id?: number
            locality_id?: number
            disabled_reason?: string
        } | null
    },
    getMapsConfig: async (surface: 'search' | 'posting' = 'search') => {
        const response = await api.get('/locations/maps-config', { params: { surface } })
        return response.data as {
            available: boolean
            provider: 'database' | 'openstreetmap' | 'google'
            map_engine: 'google' | 'openstreetmap' | 'off'
            api_key?: string | null
            fallback_reason?: string | null
            osm_fallback?: boolean
        }
    },
    reserveMapsCharge: async (body: { kind: 'map' | 'lookup'; session_id: string; country?: string; surface?: 'search' | 'posting' }) => {
        const response = await api.post('/locations/maps-charge', body)
        return response.data as { allowed: boolean; charged: boolean }
    },
    geocodeCenter: async (
        q: string,
        hints?: {
            country_code?: string
            pincode?: string
            city?: string
            state?: string
            area?: string
        },
    ) => {
        const response = await api.get('/locations/geocode-center', {
            params: { q, surface: 'posting', ...(hints || {}) },
        })
        return response.data as { lat: number; lng: number; query: string } | null
    },
    resolvePlace: async (body: {
        query: string
        country_code: string
        city_name?: string
        city_id?: number
        city_lat?: number
        city_lng?: number
    }) => {
        // Forward-geocode a typed place via PlacesService (cache + Google/OSM). Not reverse-geocode.
        const response = await api.post('/locations/resolve-place', { surface: 'posting', ...body })
        return response.data as {
            place_id?: string | null
            name: string
            formatted_address?: string | null
            lat: number
            lng: number
            area_name?: string | null
        } | null
    },
    resolvePinStreetAddress: async (lat: number, lon: number) => {
        const response = await api.get('/locations/pin-street-address', {
            params: { lat, lon },
        })
        return response.data as { formatted_address: string; lat: number; lon: number } | null
    },
    lookupPincode: async (code: string, countryCode?: string) => {
        const clean = String(code || '').trim().replace(/\s+/g, '');
        if (!clean) return { valid: false, pincode: clean };
        try {
            const response = await api.get(`/locations/pincode/${encodeURIComponent(clean)}`, {
                params: countryCode ? { country_code: countryCode } : undefined,
            });
            return response.data as {
                valid: boolean;
                pincode: string;
                city?: string | null;
                state?: string | null;
                country?: string | null;
                locality?: string | null;
                localities?: string[];
                latitude?: number | null;
                longitude?: number | null;
            };
        } catch {
            return { valid: false, pincode: clean };
        }
    },
    getSupportedCountries: async () => {
        const response = await api.get('/locations/supported-countries')
        return response.data as { countries: { name: string; code: string; state_count: number }[]; total: number }
    },
    getSupportedStates: async (country_code: string = 'IN') => {
        const response = await api.get('/locations/supported-states', { params: { country_code } })
        return response.data as { country_code: string; states: { name: string; city_count: number }[]; total: number }
    },
    getSupportedCitiesByState: async (
        state: string,
        country_code: string = 'IN',
        search: string | undefined = undefined,
        offset: number = 0,
        limit: number = 40
    ) => {
        const response = await api.get('/locations/supported-cities-by-state', {
            params: { state, country_code, search, offset, limit }
        })
        return response.data as {
            country_code: string
            state: string
            cities: string[]
            total: number
            offset: number
            limit: number
            has_more: boolean
        }
    },
    getSupportedCities: async (
        country_code: string = 'IN',
        search?: string,
        offset: number = 0,
        limit: number = 60,
    ) => {
        const response = await api.get('/locations/supported-cities', {
            params: { country_code, search, offset, limit },
        });
        return response.data as {
            country_code: string;
            cities: {
                id: number;
                name: string;
                state: string;
                state_id?: number;
                country_id?: number;
                latitude?: number;
                longitude?: number;
            }[];
            total: number;
            offset: number;
            limit: number;
            has_more: boolean;
        };
    },
    
    // Admin - Countries
    getCountries: async (skip: number = 0, limit: number = 100, search?: string) => {
        const params: any = { skip, limit }
        if (search) params.search = search
        const response = await api.get('/locations/admin/countries', { params })
        return response.data // Returns { items, total, skip, limit }
    },
    createCountry: async (data: { name: string; code: string; phone_code?: string; currency?: string; status?: number }) => {
        const response = await api.post('/locations/admin/countries', data)
        return response.data
    },
    updateCountry: async (id: number, data: { name?: string; code?: string; phone_code?: string; currency?: string; status?: number; cascade?: boolean }) => {
        const response = await api.put(`/locations/admin/countries/${id}`, data)
        return response.data
    },
    bulkUpdateCountryStatus: async (ids: number[], status: number, cascade: boolean = true) => {
        const response = await api.post('/locations/admin/countries/bulk-status', { ids, status, cascade })
        return response.data
    },
    deleteCountry: async (id: number) => {
        const response = await api.delete(`/locations/admin/countries/${id}`)
        return response.data
    },
    
    // Admin - States
    getStates: async (skip: number = 0, limit: number = 100, country_id?: number, search?: string) => {
        const params: any = { skip, limit }
        if (country_id) params.country_id = country_id
        if (search) params.search = search
        const response = await api.get('/locations/admin/states', { params })
        return response.data // Returns { items, total, skip, limit }
    },
    createState: async (data: { name: string; code: string; country_id?: number; status?: number }) => {
        const response = await api.post('/locations/admin/states', data)
        return response.data
    },
    updateState: async (id: number, data: { name?: string; code?: string; country_id?: number; status?: number; cascade?: boolean }) => {
        const response = await api.put(`/locations/admin/states/${id}`, data)
        return response.data
    },
    bulkUpdateStateStatus: async (ids: number[], status: number, cascade: boolean = true) => {
        const response = await api.post('/locations/admin/states/bulk-status', { ids, status, cascade })
        return response.data
    },
    deleteState: async (id: number) => {
        const response = await api.delete(`/locations/admin/states/${id}`)
        return response.data
    },
    
    // Admin - Cities
    getCities: async (skip: number = 0, limit: number = 100, state_id?: number, search?: string) => {
        const params: any = { skip, limit }
        if (state_id) params.state_id = state_id
        if (search) params.search = search
        const response = await api.get('/locations/admin/cities', { params })
        return response.data // Returns { items, total, skip, limit }
    },
    createCity: async (data: { 
        name: string; 
        state_id: number; 
        district_id?: number; 
        type?: string; 
        tier?: number;
        latitude?: number;
        longitude?: number;
        alternate_names?: string[];
        status?: number;
    }) => {
        const response = await api.post('/locations/admin/cities', data)
        return response.data
    },
    updateCity: async (id: number, data: { 
        name?: string; 
        state_id?: number; 
        district_id?: number; 
        type?: string; 
        tier?: number;
        latitude?: number;
        longitude?: number;
        alternate_names?: string[];
        status?: number;
        cascade?: boolean;
    }) => {
        const response = await api.put(`/locations/admin/cities/${id}`, data)
        return response.data
    },
    bulkUpdateCityStatus: async (ids: number[], status: number, cascade: boolean = true) => {
        const response = await api.post('/locations/admin/cities/bulk-status', { ids, status, cascade })
        return response.data
    },
    deleteCity: async (id: number) => {
        const response = await api.delete(`/locations/admin/cities/${id}`)
        return response.data
    },
    
    // Admin - Localities
    getLocalities: async (skip: number = 0, limit: number = 100, city_id?: number, search?: string) => {
        const params: any = { skip, limit }
        if (city_id) params.city_id = city_id
        if (search) params.search = search
        const response = await api.get('/locations/admin/localities', { params })
        return response.data // Returns { items, total, skip, limit }
    },
    createLocality: async (data: { 
        name: string; 
        city_id?: number; 
        pincode_id?: number;
        latitude?: number;
        longitude?: number;
        status?: number;
    }) => {
        const response = await api.post('/locations/admin/localities', data)
        return response.data
    },
    updateLocality: async (id: number, data: { 
        name?: string; 
        city_id?: number; 
        pincode_id?: number;
        latitude?: number;
        longitude?: number;
        status?: number;
    }) => {
        const response = await api.put(`/locations/admin/localities/${id}`, data)
        return response.data
    },
    bulkUpdateLocalityStatus: async (ids: number[], status: number) => {
        const response = await api.post('/locations/admin/localities/bulk-status', { ids, status, cascade: false })
        return response.data
    },
    deleteLocality: async (id: number) => {
        const response = await api.delete(`/locations/admin/localities/${id}`)
        return response.data
    },
}

export type PropertyTypePublic = {
    id: number
    slug: string
    display_name: string
    has_subtypes: boolean
    wizard_profile?: string
    sort_order: number
    sub_types: {
        id: number
        slug: string
        display_name: string
        flow_group: string
        sort_order: number
    }[]
}

export const propertyTypeAPI = {
    listPublic: async () => {
        const response = await api.get('/property-types')
        return response.data as { property_types: PropertyTypePublic[] }
    },
    getAdminTypes: async (skip = 0, limit = 50, search?: string) => {
        const params: Record<string, unknown> = { skip, limit }
        if (search) params.search = search
        const response = await api.get('/property-types/admin/types', { params })
        return response.data as {
            items: {
                id: number
                slug: string
                display_name: string
                sort_order: number
                has_subtypes: boolean
                wizard_profile: string
                is_active: boolean
                sub_types_count: number
            }[]
            total: number
            skip: number
            limit: number
        }
    },
    createAdminType: async (data: {
        slug: string
        display_name: string
        sort_order?: number
        has_subtypes?: boolean
        wizard_profile?: string
        is_active?: boolean
    }) => {
        const response = await api.post('/property-types/admin/types', data)
        return response.data
    },
    updateAdminType: async (
        id: number,
        data: {
            display_name?: string
            sort_order?: number
            has_subtypes?: boolean
            wizard_profile?: string
            is_active?: boolean
        }
    ) => {
        const response = await api.put(`/property-types/admin/types/${id}`, data)
        return response.data
    },
    deleteAdminType: async (id: number) => {
        await api.delete(`/property-types/admin/types/${id}`)
    },
    getAdminSubTypes: async (property_type_id?: number, skip = 0, limit = 200) => {
        const params: { skip: number; limit: number; property_type_id?: number } = { skip, limit }
        if (property_type_id != null) {
            params.property_type_id = property_type_id
        }
        const response = await api.get('/property-types/admin/sub-types', {
            params,
        })
        return response.data as {
            items: {
                id: number
                property_type_id: number
                slug: string
                display_name: string
                flow_group: string
                sort_order: number
                is_active: boolean
            }[]
            total: number
            skip: number
            limit: number
        }
    },
    createAdminSubType: async (data: {
        property_type_id: number
        slug: string
        display_name: string
        flow_group: string
        sort_order?: number
        is_active?: boolean
    }) => {
        const response = await api.post('/property-types/admin/sub-types', data)
        return response.data
    },
    updateAdminSubType: async (
        id: number,
        data: {
            display_name?: string
            flow_group?: string
            sort_order?: number
            is_active?: boolean
        }
    ) => {
        const response = await api.put(`/property-types/admin/sub-types/${id}`, data)
        return response.data
    },
    deleteAdminSubType: async (id: number) => {
        await api.delete(`/property-types/admin/sub-types/${id}`)
    },
    getAdminKeywords: async (search?: string) => {
        const params: Record<string, unknown> = {}
        if (search) params.search = search
        const response = await api.get('/property-types/admin/keywords', { params })
        return response.data as {
            items: {
                property_type_slug: string
                display_name: string
                requires_bedrooms: boolean
                total_keywords: number
                keywords: {
                    id: number
                    keyword: string
                    language: string
                    is_custom: boolean
                }[]
                value?: string
                label?: string
                aliases?: string[]
            }[]
            total: number
        }
    },
    addAdminKeyword: async (data: { property_type_slug: string; keyword: string; language?: string }) => {
        const response = await api.post('/property-types/admin/keywords', data)
        return response.data
    },
    deleteAdminKeyword: async (id: number) => {
        await api.delete(`/property-types/admin/keywords/${id}`)
    },
    getAdminWidgets: async () => {
        const response = await api.get('/property-types/admin/widgets')
        return response.data as {
            widgets: {
                key: string
                label: string
                widget_type: string
                options?: string[]
                placeholder?: string
                description?: string
                is_custom: boolean
                is_builtin: boolean
                is_searchable?: boolean
                edit_placement?: string
                color?: string
                color_hex?: string
            }[]
            mappings: Record<string, string[]>
            property_types: {
                slug: string
                display_name: string
                wizard_profile?: string
            }[]
            commercial_subtypes?: {
                slug: string
                label: string
                flow_group?: string
                description?: string
            }[]
            edit_placements?: { value: string; label: string }[]
            widget_colors?: { value: string; label: string; hex: string }[]
        }
    },
    createAdminWidget: async (data: {
        key: string
        label: string
        widget_type: string
        options?: string[]
        placeholder?: string
        description?: string
        enable_for_types?: string[]
        is_searchable?: boolean
        edit_placement?: string
        color?: string
    }) => {
        const response = await api.post('/property-types/admin/widgets', data)
        return response.data
    },
    updateAdminWidget: async (
        widget_key: string,
        data: {
            label?: string
            widget_type?: string
            options?: string[]
            placeholder?: string
            description?: string
            is_searchable?: boolean
            edit_placement?: string
            color?: string
        }
    ) => {
        const response = await api.put(`/property-types/admin/widgets/${widget_key}`, data)
        return response.data
    },
    deleteAdminWidget: async (widget_key: string) => {
        await api.delete(`/property-types/admin/widgets/${widget_key}`)
    },
    updatePropertyTypeWidgetMapping: async (property_type_slug: string, enabled_widgets: string[]) => {
        const response = await api.put(`/property-types/admin/widgets/mapping/${property_type_slug}`, {
            enabled_widgets,
        })
        return response.data
    },
    resetPropertyTypeWidgetMapping: async (property_type_slug: string) => {
        const response = await api.post(`/property-types/admin/widgets/mapping/${property_type_slug}/reset`)
        return response.data
    },
    getSearchableWidgets: async (property_type?: string, subtype?: string) => {
        const params: Record<string, string> = {}
        if (property_type) params.property_type = property_type
        if (subtype) params.subtype = subtype
        const response = await api.get('/property-types/widgets/searchable', { params })
        return response.data as {
            widgets: {
                key: string
                label: string
                widget_type: string
                options?: string[]
                placeholder?: string
                description?: string
                is_custom: boolean
                is_builtin: boolean
                is_searchable: boolean
            }[]
        }
    },
    getWidgetsForEdit: async (property_type?: string, subtype?: string) => {
        const params: Record<string, string> = {}
        if (property_type) params.property_type = property_type
        if (subtype) params.subtype = subtype
        const response = await api.get('/property-types/widgets/for-edit', { params })
        return response.data as {
            widgets: {
                key: string
                label: string
                widget_type: string
                options?: string[]
                placeholder?: string
                description?: string
                is_custom?: boolean
                edit_placement?: string
            }[]
            edit_placements?: { value: string; label: string }[]
        }
    },
    toggleWidgetSearchable: async (widget_key: string, is_searchable: boolean) => {
        const response = await api.put(`/property-types/admin/widgets/${widget_key}/searchable`, {
            is_searchable,
        })
        return response.data
    },
}

export const propertyTypeAdminAPI = propertyTypeAPI;

export default api;
